import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="trix-char-count"
// Contador de caracteres para los editores Trix restringidos a negrita.
//
// `modo` decide QUÉ se cuenta, y no es cosmético: tiene que medir lo mismo que valida el
// servidor, o el editor ve un número que no explica el error al guardar.
//   'visible'    → solo el texto, sin etiquetas. Es el disclaimer (ENH-RNK-3796): su columna
//                  es `text`, así que el límite lo pone solo el modelo sobre el texto visible.
//   'almacenado' → el HTML tal y como se guarda, marcado incluido. Son las frases destacadas
//                  (ENH-RNK-4081): su columna es `varchar(255)` y es la BD quien pone el techo,
//                  así que contar el texto visible dejaría pasar valores que no caben.
export default class extends Controller {
  static targets = ['counter', 'editor']
  static values = { max: Number, modo: { type: String, default: 'visible' } }

  initialize () {
    this.longitudPrevia = 0

    // Corre después de `evitarSuperarLimite` en cada `trix-change`, así que registra la
    // longitud ya definitiva. Es el único sitio que actualiza `longitudPrevia`: si el guard
    // la llevara por su cuenta, `trix-initialize` la dejaría desfasada en 0.
    this.updateCount = event => {
      if (event.target !== this.editorTarget) return

      this.registrarEstado(this.longitud(event.target))
    }

    // Frena solo lo que HACE CRECER el valor. Si el contenido ya venía por encima del límite
    // —frases guardadas antes de este tope, o a las que Trix suma su `<div>` al abrirlas—,
    // deshacer también los borrados dejaría el campo ineditable: ni escribir ni corregir.
    this.evitarSuperarLimite = event => {
      if (event.target !== this.editorTarget) return

      const longitud = this.longitud(event.target)
      if (longitud > this.maxValue && longitud > this.longitudPrevia) event.target.editor.undo()
    }
  }

  connect () {
    document.addEventListener('trix-initialize', this.updateCount)
    document.addEventListener('trix-change', this.evitarSuperarLimite)
    document.addEventListener('trix-change', this.updateCount)

    this.sincronizarEstadoInicial()
  }

  // `trix-initialize` puede haber saltado antes de que Stimulus conecte este controller, y
  // entonces el contador se queda en blanco hasta el primer cambio. Si ya tenemos editor,
  // pintamos el estado de partida; si no, lo hará el propio `trix-initialize`.
  sincronizarEstadoInicial () {
    if (!this.hasEditorTarget || !this.editorTarget.editor) return

    this.registrarEstado(this.longitud(this.editorTarget))
  }

  registrarEstado (longitud) {
    this.longitudPrevia = longitud
    this.counterTarget.textContent = `${longitud}/${this.maxValue}`
  }

  disconnect () {
    document.removeEventListener('trix-initialize', this.updateCount)
    document.removeEventListener('trix-change', this.evitarSuperarLimite)
    document.removeEventListener('trix-change', this.updateCount)
  }

  longitud (elemento) {
    if (this.modoValue === 'almacenado') return this.textoAlmacenado(elemento).length

    return this.textoVisible(elemento.editor).length
  }

  // `value` del `<trix-editor>` lee el input oculto que Trix mantiene sincronizado: es
  // literalmente lo que viaja en el POST y acaba en la columna, así que es la única medida
  // fiable cuando el techo lo impone la base de datos.
  textoAlmacenado (elemento) {
    return elemento.value || ''
  }

  textoVisible (editor) {
    return editor.getDocument().toString().replace(/\n+$/, '')
  }
}
